import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchAdminContent,
  saveAdminSection,
  getAdminToken,
  setAdminToken,
  isAdminSessionError,
} from '../content/contentApi';
import { normalizeContactPage } from '../content/contactPageTemplate';
import EditableContactPage from '../components/contact/EditableContactPage';
import EditorTopBar from '../components/services/editable/EditorTopBar';
import './Admin.css';

/**
 * Dedicated full-viewport route for inline WYSIWYG editing of the (singleton)
 * Contact page — mirrors AboutPageEditor.jsx exactly (no slug/list/create
 * step, no embedded shared CRUD panels needed since this page has no
 * cross-page-shared data).
 */
export default function ContactPageEditor() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null); // { data, styles }
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(false);
  const [status, setStatus] = useState('');

  const handleSessionExpired = useCallback(() => {
    setAdminToken(null);
    navigate('/admin/login?reason=session', { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const api = await fetchAdminContent();
      const doc = normalizeContactPage(api.contactPage);
      setDraft({ data: doc.data, styles: doc.styles });
      setDirty(false);
    } catch (e) {
      if (isAdminSessionError(e)) {
        handleSessionExpired();
        return;
      }
      setError(e.message || 'Failed to load the Contact page.');
    } finally {
      setLoading(false);
    }
  }, [handleSessionExpired]);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate('/admin/login', { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  const handleChange = (patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    setError('');
    try {
      const now = new Date().toISOString();
      const nextSection = { data: draft.data, styles: draft.styles, updatedAt: now };
      await saveAdminSection('contactPage', nextSection);
      setDirty(false);
      setStatus('Saved · the public /contact page now matches this.');
    } catch (e) {
      if (isAdminSessionError(e)) {
        handleSessionExpired();
        return;
      }
      setError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !draft) {
    return (
      <div className="admin-root">
        <div className="admin-loading">
          <p>Loading page editor…</p>
        </div>
      </div>
    );
  }

  const statusLabel = saving ? 'Saving…' : dirty ? 'Unsaved changes' : status || 'No changes yet';

  return (
    <div className="admin-root">
      <EditorTopBar
        title="Contact Page"
        preview={preview}
        onTogglePreview={() => setPreview((p) => !p)}
        onSave={handleSave}
        saving={saving}
        statusLabel={statusLabel}
        onBack={() => navigate('/admin')}
      />
      {error ? <p className="admin-error" style={{ padding: '10px 20px', margin: 0 }}>{error}</p> : null}
      <EditableContactPage data={draft.data} styles={draft.styles} editable preview={preview} onChange={handleChange} />
    </div>
  );
}
